import { memo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { i18n as I18n } from 'builder/utils/i18n'
import { actions, selectors } from 'builder/modules/resumeEditor'
import {
  AIResumeDraftEventLabels,
  AIResumeDraftEvents,
  AIResumeLinkedInSteps,
} from 'builder/modules/constants'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import noPhotoCompositionWithoutAvatarImage from '../../CreateResumeWithLinkedin/assets/no-photo-composition_without_avatar.svg'
import noPhotoCompositionImage from '../../CreateResumeWithLinkedin/assets/no-photo-composition.png'
import doodle from '../../CreateResumeWithLinkedin/assets/doodle.svg'
import {
  LinkedInProfileCard,
  Actions,
  TryAnotherLink,
  Revert,
  GenerateButton,
  ProfileContainer,
  ImageWrapper,
  LinkedInImage,
  LinkedInImageDoodle,
  InfoContainer,
  FullName,
  Position,
  ProfileHeaderWrapper,
  Title,
  SubTitle,
  LinkedinProfileAvatar,
} from './styles'
import { LINKEDIN_LOCALIZATION_KEY } from './constants'

const LinkedInProfile = () => {
  const dispatch = useDispatch()
  const resume = useTypedSelector(selectors.resume)

  useEffect(() => {
    trackInternalEvent(AIResumeDraftEvents.seeLinkedInProfileScreen, {
      label: AIResumeDraftEventLabels.aiResume,
    })
  }, [])

  const { linkedInProfileData, linkedInProfileURL } = useTypedSelector(
    store => store.resumeEditor.aiResume.linkedInData,
  )

  const { avatar, fullName, position } = linkedInProfileData
  const onTryAnotherLink = () => {
    trackInternalEvent(AIResumeDraftEvents.clickTryAgain, {
      label: AIResumeDraftEventLabels.linkedInProfileScreen,
    })
    dispatch(actions.setAIResumeLinkedInStep(AIResumeLinkedInSteps.linkedInPrefill))
  }

  const onGenerateClick = () => {
    trackInternalEvent(AIResumeDraftEvents.clickGenerate, {
      label: AIResumeDraftEventLabels.linkedInProfileScreen,
    })
    if (resume) {
      let updatedUrl = linkedInProfileURL.toLowerCase()
      // If protocol was not provided, add it.
      updatedUrl =
        updatedUrl.startsWith('https') || updatedUrl.startsWith('http')
          ? updatedUrl
          : `https://${updatedUrl}`
      // The regular expression doesn't match the URL if it doesn't start with www.
      // So, we need to add www. to the URL if it doesn't have it.
      updatedUrl = updatedUrl.replace(/:\/\/(?!www\.)/, '://www.')
      dispatch(
        actions.generateResumeFromLinkedIn({
          linkedInProfileURL: updatedUrl,
          resumeId: resume.id,
        }),
      )
    }
  }

  return (
    <>
      <ProfileContainer>
        <LinkedInProfileCard>
          <ImageWrapper>
            {avatar ? (
              <>
                <LinkedinProfileAvatar
                  src={noPhotoCompositionWithoutAvatarImage}
                  alt="LinkedIn Avatar"
                />
                <LinkedInImage src={avatar} alt="User LinkedIn Avatar" />
                <LinkedInImageDoodle src={doodle} alt="LinkedIn Avatar Doodle" />
              </>
            ) : (
              <LinkedinProfileAvatar src={noPhotoCompositionImage} alt="LinkedIn Avatar" />
            )}
          </ImageWrapper>
          <InfoContainer>
            <FullName>{fullName}</FullName>
            <Position>{position}</Position>
          </InfoContainer>
        </LinkedInProfileCard>
        <ProfileHeaderWrapper>
          <Title>
            {I18n.t(`${LINKEDIN_LOCALIZATION_KEY}.validate_linkedin_profile_modal.title`)}
          </Title>
          <SubTitle>
            {I18n.t(`${LINKEDIN_LOCALIZATION_KEY}.validate_linkedin_profile_modal.description`)}
          </SubTitle>
        </ProfileHeaderWrapper>
      </ProfileContainer>
      <Actions>
        <TryAnotherLink onClick={onTryAnotherLink}>
          <Revert />
          {I18n.t('builder.ai_resume_draft.use_another_link')}
        </TryAnotherLink>
        <GenerateButton onClick={onGenerateClick}>
          {I18n.t(
            `${LINKEDIN_LOCALIZATION_KEY}.validate_linkedin_profile_modal.continue_button_text`,
          )}
        </GenerateButton>
      </Actions>
    </>
  )
}

export default memo(LinkedInProfile)
